import {
  collection,
  deleteDoc,
  deleteField,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import { signInAnonymously } from "firebase/auth";
import dayjs from "dayjs";
import { auth, firestore } from "../config";
import { ModelReviewState } from "../../../models/Utils";
import { getRemoteNumber, RemotePrams } from "../remote_config";

export const getTributeMinChars = () => getRemoteNumber(RemotePrams.LIMIT_TRIBUTE_MIN_CHARACTERS) || 30;
export const getTributeMaxChars = () => getRemoteNumber(RemotePrams.LIMIT_TRIBUTE_MAX_CHARACTERS) || 500;

// COLLECTIONS
const endpointStories = (id: string) => {
  return collection(firestore, "public", id, "stories");
};

export const getTributes = async (memorialId: string) => {
  return getDocs(
    query(endpointStories(memorialId), where("state", "==", ModelReviewState.PUBLIC), orderBy("created", "desc"))
  ).then((d) => {
    return d.docs.map((d) => {
      const trib = d.data() as ModelTribute;
      return { ...trib, id: d.id };
    });
  });
};

export const getTribute = async (memorialId: string, id: string) => {
  return getDoc(doc(endpointStories(memorialId), id)).then((d) => d?.data() as ModelTribute);
};

// MODERATION - a memorial's own admins reviewing every submitted tribute
// (not just the already-PUBLIC ones getTributes returns for the public page).

export const getMemorialTributes = async (memorialId: string) => {
  return getDocs(query(endpointStories(memorialId), orderBy("created", "desc")))
    .then((d) => d.docs.map((ds) => ({ ...(ds.data() as ModelTribute), id: ds.id })))
    .catch((e) => {
      console.error(e.message);
      return [] as ModelTribute[];
    });
};

// A count-only aggregate query (no documents downloaded) - used by the
// Memorials list's pending-tributes badge, which previously called
// getMemorialTributes and threw away everything but the count, downloading
// every tribute on every approved memorial just to size a badge.
export const getMemorialPendingTributeCount = async (memorialId: string) => {
  return getCountFromServer(query(endpointStories(memorialId), where("state", "==", ModelReviewState.WAITING_FOR_REVIEW)))
    .then((snap) => snap.data().count)
    .catch((e) => {
      console.error(e.message);
      return 0;
    });
};

export const approveTribute = async (memorialId: string, tributeId: string) => {
  await updateDoc(doc(endpointStories(memorialId), tributeId), { state: ModelReviewState.PUBLIC });
};

export const deleteTribute = async (memorialId: string, tributeId: string) => {
  await deleteDoc(doc(endpointStories(memorialId), tributeId));
};

// A non-owner admin/manager can never delete a tribute outright - they can
// only flag it for the owner's review. Only the owner (see deleteTribute)
// can actually remove it.
export const requestTributeDeletion = async (memorialId: string, tributeId: string, requestedBy: string) => {
  await updateDoc(doc(endpointStories(memorialId), tributeId), {
    deletion_requested: true,
    deletion_requested_by: requestedBy,
    deletion_requested_at: Timestamp.now(),
  });
};

export const dismissTributeDeletionRequest = async (memorialId: string, tributeId: string) => {
  await updateDoc(doc(endpointStories(memorialId), tributeId), {
    deletion_requested: deleteField(),
    deletion_requested_by: deleteField(),
    deletion_requested_at: deleteField(),
  });
};

// RATE LIMITING - one tribute per memorial per day, enforced server-side by
// Firestore Security Rules against a real Firebase Auth uid, not a
// localStorage value a visitor could edit in devtools. Leaving a tribute
// needs no account, so if nobody's signed in yet this silently creates an
// invisible Anonymous Auth session (no prompt, no UI) to get a stable uid;
// if someone's already signed in for real, their own uid is reused instead.
// This never fires when a session already exists (real or anonymous), so it
// can never sign a real user out of their account.
// Exported for reuse by other rate-limited writes a visitor can make with no
// account (see endpoints/reports.tsx) - same identity, same guarantee.
export const ensureRateLimitUid = async (): Promise<string> => {
  if (auth.currentUser) return auth.currentUser.uid;
  const credential = await signInAnonymously(auth);
  return credential.user.uid;
};

// public/{memorialId}/tribute_limits/{uid} holds only a last_submitted
// server timestamp - the Firestore rule on the stories/{id} create checks
// this doc (via get()) is either absent or over 24h old before allowing a
// new tribute, and the rule on this doc itself only lets a uid write its
// own entry. See the Firestore rules handoff for the exact rule text.
const tributeLimitDoc = (memorialId: string, uid: string) =>
  doc(collection(firestore, "public", memorialId, "tribute_limits"), uid);

// A same-device, same-browser cooldown hint only - lets the button show a
// friendly "come back tomorrow" message before someone fills out the whole
// form, rather than always opening the modal and only failing at the very
// end. It is NOT the security boundary (see ensureRateLimitUid/
// tributeLimitDoc above and the Firestore rule for that): clearing this key
// just means the real, server-enforced limit rejects the write instead, and
// createTribute below shows the same friendly message in that case too.
const tributeCooldownKey = (memorialId: string) => `tribute_cooldown_${memorialId}`;
const TRIBUTE_COOLOFF_HOURS = 24;

export const isTributeOnCooldown = (memorialId: string) =>
  dayjs(Number(localStorage.getItem(tributeCooldownKey(memorialId))) || 0).isAfter(dayjs());

// The exact moment the cooldown above lifts - undefined if there isn't one
// on file (isTributeOnCooldown is false, or nothing was ever recorded).
export const getTributeCooldownExpiry = (memorialId: string) => {
  const raw = Number(localStorage.getItem(tributeCooldownKey(memorialId))) || 0;
  return raw && dayjs(raw).isAfter(dayjs()) ? dayjs(raw) : undefined;
};

export class TributeCooldownError extends Error {}

export const createTribute = async (id: string, message: string, name: string, autoApprove: boolean) => {
  const uid = await ensureRateLimitUid();
  const data: ModelTribute = {
    created: Timestamp.fromDate(new Date()),
    description: message,
    author_name: name,
    memorial_id: id,
    // Must match the stories create rule's own check against the memorial's
    // tributes_auto_approve field - the client picking a state here is only
    // ever a convenience, never the actual authorization.
    state: autoApprove ? ModelReviewState.PUBLIC : ModelReviewState.WAITING_FOR_REVIEW,
  };
  const storyRef = doc(collection(firestore, "public", id, "stories"));
  const batch = writeBatch(firestore);
  batch.set(storyRef, data);
  batch.set(tributeLimitDoc(id, uid), { last_submitted: serverTimestamp() });

  try {
    await batch.commit();
  } catch (e: any) {
    // The rate limit is the only per-request condition on this write, so a
    // rejection here is treated as the cooldown - a reasonable assumption,
    // not a certainty, since Firestore doesn't say which rule condition
    // failed.
    if (e.code === "permission-denied") throw new TributeCooldownError();
    throw e;
  }

  localStorage.setItem(`tr_id_${id}`, storyRef.id);
  localStorage.setItem(tributeCooldownKey(id), `${dayjs().add(TRIBUTE_COOLOFF_HOURS, "hours").valueOf()}`);

  return { ...data, id: storyRef.id };
};

export interface ModelTribute {
  id?: string;
  created: Timestamp;
  author_name: string;
  description: string;
  memorial_id: string;
  state: ModelReviewState;
  // Set by a non-owner admin/manager requesting this tribute be removed -
  // only the owner can actually delete it (see deleteTribute); this just
  // flags it for their review until they confirm or dismiss the request.
  deletion_requested?: boolean;
  deletion_requested_by?: string;
  deletion_requested_at?: Timestamp;
}
